const SOURCE_SELECTOR = '[data-stamped-source], #stamped-main-widget';
const COUNT_SELECTOR = '.stamped-summary-text-1[data-count], meta[itemprop="reviewCount"]';
const STYLE_ID = 'stamped-review-total-styles';
const STAMPED_WIDGET_SCRIPT_ID = 'stamped-script-widget';
const STAMPED_WIDGET_API = 'https://stamped.io/api/widget';
const CACHE_KEY = 'stamped-published-review-total';
const CACHE_TTL_MS = 60 * 60 * 1000;
const FETCH_TIMEOUT_MS = 8000;
const STAMPED_FN_POLL_MS = 50;
const STAMPED_FN_MAX_WAIT_MS = 15000;

let reviewTotalRequest = null;
let stampedScriptRequest = null;

const parseReviewCount = (value) => {
  const count = Number(String(value || '').replace(/[^\d]/g, ''));
  return Number.isFinite(count) && count > 0 ? count : null;
};

const formatReviewCount = (value) => Number(value).toLocaleString('en-US');

const roundReviewCount = (value) => formatReviewCount(Math.floor(Number(value) / 1000) * 1000);

const readCount = (source) => {
  const node = source.querySelector(COUNT_SELECTOR);
  if (!node) return null;

  return parseReviewCount(node.getAttribute('data-count') || node.getAttribute('content'));
};

const readCountFromHtml = (html) => {
  if (!html) return null;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  return readCount(doc);
};

const parseCountFromWidgetPayloadText = (text) => {
  const countMatch = text.match(/"count"\s*:\s*(\d+)/);
  if (countMatch) return parseReviewCount(countMatch[1]);

  const reviewCountMatch = text.match(/itemprop=['"]reviewCount['"]\s+content=['"](\d+)['"]/);
  if (reviewCountMatch) return parseReviewCount(reviewCountMatch[1]);

  return null;
};

const getStampedScriptTag = () => document.getElementById(STAMPED_WIDGET_SCRIPT_ID);

const getStampedCredentials = () => {
  const scriptTag = getStampedScriptTag();
  const apiKey = scriptTag?.getAttribute('data-api-key') || window.StampedGlobalOptions?.apiKey;
  const storeUrl = window.Shopify?.shop || window.StampedGlobalOptions?.storeUrl;

  if (!apiKey || !storeUrl) return null;

  return { apiKey, storeUrl };
};

const waitForStampedFn = (timeoutMs = STAMPED_FN_MAX_WAIT_MS) =>
  new Promise((resolve) => {
    if (window.StampedFn) {
      resolve(true);
      return;
    }

    const started = Date.now();

    const tick = () => {
      if (window.StampedFn) {
        resolve(true);
        return;
      }

      if (Date.now() - started >= timeoutMs) {
        resolve(false);
        return;
      }

      setTimeout(tick, STAMPED_FN_POLL_MS);
    };

    tick();
  });

const loadScriptElement = (script) =>
  new Promise((resolve, reject) => {
    const finishWhenReady = async () => {
      await waitForStampedFn();
      resolve();
    };

    if (script.getAttribute('src')) {
      if (window.StampedFn) {
        resolve();
        return;
      }

      script.addEventListener('load', () => finishWhenReady(), { once: true });
      script.addEventListener('error', () => reject(new Error('Stamped script failed to load')), { once: true });
      return;
    }

    const src = script.getAttribute('data-src');
    if (!src) {
      reject(new Error('Stamped script has no src'));
      return;
    }

    if (script.hasAttribute('data-stamped-promoted')) {
      finishWhenReady();
      return;
    }

    script.setAttribute('data-stamped-promoted', '');
    script.setAttribute('src', src);
    script.removeAttribute('type');
    script.addEventListener('load', () => finishWhenReady(), { once: true });
    script.addEventListener('error', () => reject(new Error('Stamped script failed to load')), { once: true });
  });

const ensureStampedScript = () => {
  stampedScriptRequest ??= (async () => {
    if (window.StampedFn) return;

    const scriptTag = getStampedScriptTag();
    if (!scriptTag) {
      await waitForStampedFn(3000);
      return;
    }

    await loadScriptElement(scriptTag);
    await waitForStampedFn();
  })();

  return stampedScriptRequest;
};

const readCachedTotal = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const cached = JSON.parse(raw);
    if (!cached?.count || !cached?.ts) return null;
    if (Date.now() - cached.ts > CACHE_TTL_MS) return null;

    return parseReviewCount(cached.count);
  } catch {
    return null;
  }
};

const writeCachedTotal = (count) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ count, ts: Date.now() }));
  } catch {
    /* ignore quota / private mode */
  }
};

const fetchWithTimeout = (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timeoutId));
};

// Published total from main widget summary (GET /api/widget, empty productId).
const fetchPublishedTotalFromWidgetApi = async () => {
  const credentials = getStampedCredentials();
  if (!credentials) return null;

  const params = new URLSearchParams({
    apiKey: credentials.apiKey,
    storeUrl: credentials.storeUrl,
    productId: '',
    page: '1',
    take: '1',
  });

  try {
    const response = await fetchWithTimeout(`${STAMPED_WIDGET_API}?${params.toString()}`, {
      credentials: 'omit',
    });
    if (!response.ok) return null;

    const text = await response.text();
    const fromText = parseCountFromWidgetPayloadText(text);
    if (fromText != null) return fromText;

    const payload = JSON.parse(text);
    const fromCount = parseReviewCount(payload?.count);
    if (fromCount != null) return fromCount;

    return readCountFromHtml(payload?.product) ?? readCountFromHtml(payload?.widget);
  } catch {
    return null;
  }
};

const revalidateCachedTotal = () => {
  fetchPublishedTotalFromWidgetApi().then((count) => {
    if (count != null) writeCachedTotal(count);
  });
};

const injectHiddenSource = () => {
  const source = document.createElement('div');
  source.id = 'stamped-main-widget';
  source.hidden = true;
  source.setAttribute('aria-hidden', 'true');
  source.setAttribute('data-stamped-injected', '');
  source.setAttribute('data-product-id', '');
  document.body.append(source);

  window.StampedFn?.loadWidget?.();

  return source;
};

const waitForCount = (source) =>
  new Promise((resolve) => {
    const initialCount = readCount(source);

    if (initialCount != null) {
      resolve(initialCount);
      return;
    }

    const observer = new MutationObserver(() => tryRead());

    const finish = (count) => {
      observer.disconnect();
      document.removeEventListener('stamped:reviews:loaded', tryRead);
      resolve(count);
    };

    function tryRead() {
      const count = readCount(source);
      if (count != null) finish(count);
    }

    observer.observe(source, { childList: true, subtree: true, attributes: true });
    document.addEventListener('stamped:reviews:loaded', tryRead);
  });

const fetchTotalFromWidgetDom = async () => {
  const existing = document.querySelector(SOURCE_SELECTOR);
  if (existing && !existing.hasAttribute('data-stamped-injected')) {
    const count = readCount(existing);
    if (count != null) return count;
  }

  await ensureStampedScript();

  const source = document.querySelector(SOURCE_SELECTOR) ?? injectHiddenSource();
  const count = await waitForCount(source);

  if (source.hasAttribute('data-stamped-injected')) source.remove();

  return count;
};

const resolveReviewTotal = async () => {
  const cached = readCachedTotal();
  if (cached != null) {
    revalidateCachedTotal();
    return cached;
  }

  const fromApi = await fetchPublishedTotalFromWidgetApi();
  if (fromApi != null) {
    writeCachedTotal(fromApi);
    return fromApi;
  }

  const fromDom = await fetchTotalFromWidgetDom();
  if (fromDom != null) writeCachedTotal(fromDom);

  return fromDom;
};

const getReviewTotal = () => {
  reviewTotalRequest ??= resolveReviewTotal();
  return reviewTotalRequest;
};

const ensureStyles = () => {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    stamped-review-total.stamped-review-total--pending {
      opacity: 0.92;
    }
  `;
  document.head.append(style);
};

class StampedReviewTotal extends HTMLElement {
  #resolveFallback() {
    const fromDom = this.textContent.trim();
    if (!this.dataset.fallback && fromDom) {
      this.dataset.fallback = fromDom;
    }
    return this.dataset.fallback || fromDom;
  }

  #setPending(isPending) {
    if (isPending) {
      this.classList.add('stamped-review-total--pending');
      this.setAttribute('aria-busy', 'true');
      return;
    }

    this.classList.remove('stamped-review-total--pending');
    this.removeAttribute('aria-busy');
  }

  #displayTotal(total) {
    const formatted = this.dataset.format === 'full' ? formatReviewCount(total) : roundReviewCount(total);
    this.textContent = `${formatted}${this.dataset.suffix || ''}`;
  }

  async connectedCallback() {
    ensureStyles();
    this.#resolveFallback();
    this.#setPending(true);

    const total = await getReviewTotal();
    if (!this.isConnected) return;

    this.#setPending(false);

    if (total) {
      this.#displayTotal(total);
      return;
    }

    const fallback = this.#resolveFallback();
    if (fallback) this.textContent = fallback;
  }
}

if (!customElements.get('stamped-review-total')) {
  customElements.define('stamped-review-total', StampedReviewTotal);
}
